// Location.parentLocation uses Jackson's @JsonIdentityInfo (identity references) on the
// backend: the first time a given location appears ANYWHERE in a serialized response — as a
// top-level array entry, or nested as another row's parentLocation — it's a full object;
// every later reference to that SAME location collapses to just its bare id number. Because
// results are sorted by locationName by default, a warehouse can easily sort before its own
// office's POINT record, meaning the office's own top-level array entry is what collapses to
// a bare number. Any code that reads raw fields (locationType, officeCode, ...) directly off
// array entries has to go through reconstructLocationList() first, or it will silently drop
// whichever entries lost the coin-flip and became bare numbers.
export const resolveParentLocationId = (location) => {
  const parent = location?.parentLocation;
  if (parent == null) return null;
  return typeof parent === "object" ? parent.id : parent;
};

/**
 * Replaces every bare-id-collapsed entry (top-level or nested as a parentLocation) with the
 * full object, resolved from wherever that full object actually appears in the list. Entries
 * that can't be resolved (shouldn't normally happen) are dropped rather than left as unusable
 * bare numbers.
 */
export const reconstructLocationList = (locations) => {
  const byId = {};
  locations.forEach((loc) => {
    if (loc && typeof loc === "object" && loc.id != null) {
      byId[loc.id] = loc;
    }
    if (loc && typeof loc === "object" && loc.parentLocation && typeof loc.parentLocation === "object") {
      byId[loc.parentLocation.id] = loc.parentLocation;
    }
  });

  return locations
    .map((loc) => (loc && typeof loc === "object" ? loc : byId[loc]))
    .filter(Boolean);
};

/**
 * Groups locations that have a parent by the OFFICE CODE OF THAT PARENT (not their own
 * officeCode) — i.e. facilities (warehouses, terminals, etc.) nest under whichever office
 * owns the parent/"shipper" location they're linked to via parentId, not by matching their
 * own officeCode directly.
 */
export const groupChildLocationsByParentOfficeCode = (locations) => {
  const resolved = reconstructLocationList(locations);
  const byId = {};
  resolved.forEach((loc) => { byId[loc.id] = loc; });

  const map = {};
  resolved.forEach((loc) => {
    const parentId = resolveParentLocationId(loc);
    if (parentId == null) return;

    const parentOfficeCode = byId[parentId]?.officeCode;
    if (!parentOfficeCode) return;

    if (!map[parentOfficeCode]) map[parentOfficeCode] = [];
    map[parentOfficeCode].push(loc);
  });
  return map;
};

/**
 * The facilities a Location dropdown should offer: repaired POINT rows that have a name. The
 * backend also saves every shipment-stop address as an unnamed POINT owned by the shipper's
 * office (LocationServiceImpl's address nodes), and those would otherwise render as hundreds of
 * blank, unselectable entries. WAREHOUSE rows are children picked in the Warehouse dropdown;
 * GEOGRAPHY rows are internal rate-shopping graph nodes.
 */
export const selectablePointLocations = (locations) =>
  reconstructLocationList(locations).filter(
    (loc) => loc.locationType === "POINT" && String(loc.locationName ?? "").trim() !== ""
  );

/** Top-level locations — no parent — the ones that belong in a flat/general location list. */
export const filterTopLevelLocations = (locations) =>
  reconstructLocationList(locations).filter((loc) => resolveParentLocationId(loc) == null);
